import { defineStore } from 'pinia'
import type { AuthState, User } from '../types'
import { userService } from '@/services/userService'

const TOKEN_KEY = 'quran-token'

export const useAuthStore = defineStore('auth', {
  state: (): AuthState => ({
    user: null,
    token: localStorage.getItem(TOKEN_KEY),
    isAuthenticated: !!localStorage.getItem(TOKEN_KEY),
    loading: false,
  }),

  getters: {
    /**
     * Get current user
     */
    currentUser: (state): User | null => state.user,

    /**
     * Check if user is authenticated
     */
    authenticated: (state): boolean => state.isAuthenticated && !!state.token,

    /**
     * Get auth token
     */
    authToken: (state): string | null => state.token,
  },

  actions: {
    /**
     * Set authentication token
     */
    setToken(token: string) {
      this.token = token
      this.isAuthenticated = true
      localStorage.setItem(TOKEN_KEY, token)
    },

    /**
     * Set current user
     */
    setUser(user: User) {
      this.user = user
      this.isAuthenticated = true
    },

    /**
     * Login action - sets both token and user
     */
    login(token: string, user?: User) {
      this.setToken(token)
      if (user) {
        this.setUser(user)
      }
    },

    /**
     * Logout action - clears all auth data
     */
    logout() {
      this.user = null
      this.token = null
      this.isAuthenticated = false
      localStorage.removeItem(TOKEN_KEY)
    },

    /**
     * Fetch current user details
     */
    async fetchCurrentUser() {
      try {
        const user = await userService.getCurrentUser()
        if (user) {
          this.setUser(user)
        }
        return user
      } catch (error) {
        console.error('Error fetching current user:', error)
        return null
      }
    },

    /**
     * Check authentication status on app init
     */
    async checkAuth() {
      const token = localStorage.getItem(TOKEN_KEY)
      if (token) {
        this.token = token
        this.isAuthenticated = true
        // Fetch user profile
        await this.fetchCurrentUser()
      } else {
        this.logout()
      }
    },

    /**
     * Set loading state
     */
    setLoading(loading: boolean) {
      this.loading = loading
    },
  },
})
